import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api, { authApi } from '../services/api';

// Create the Auth Context
const AuthContext = createContext();

// Custom hook to use the Auth Context
export const useAuth = () => useContext(AuthContext);

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Define fetchUserData with useCallback
  const fetchUserData = useCallback(async (token) => {
    try {
      setLoading(true);
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      const response = await authApi.get('/user');
      setUser(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Session expired. Please login again.');
      logout();
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUserData(token);
    } else {
      setLoading(false);
    }
  }, [fetchUserData]);  // Add fetchUserData as a dependency
  
  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await authApi.post('/register', userData);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      setError(null);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await authApi.post('/login', { email, password });
      const { token } = response.data;

      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setError(null);
      await fetchUserData(token);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    delete authApi.defaults.headers.common['Authorization'];
    setUser(null);
    setError(null);
  };
  
  // The context value that will be provided
  const value = {
    user,
    setUser,
    fetchUserData,
    loading,
    error,
    register,
    login,
    logout,
    isAuthenticated: !!user
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 